import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, applyActionCode, checkActionCode } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { toast } from 'sonner';
import { CheckCircle, AlertCircle, Loader, Mail } from 'lucide-react';

const VerifyEmailPage = () => {
  const [isValidating, setIsValidating] = useState(true);
  const [isValidCode, setIsValidCode] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [email, setEmail] = useState('');
  const [oobCode, setOobCode] = useState('');
  const navigate = useNavigate();
  const auth = getAuth();

  // Validate the action code when component mounts
  useEffect(() => {
    const validateCode = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const mode = urlParams.get('mode');
      const code = urlParams.get('oobCode');

      if (mode !== 'verifyEmail' || !code) {
        toast.error('Invalid verification link');
        setIsValidating(false);
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      try {
        // Check if the code is valid
        const info = await checkActionCode(auth, code);
        setEmail(info.data.email);
        setOobCode(code);
        setIsValidCode(true);
        setIsValidating(false);
      } catch (error) {
        console.error('Error validating code:', error);
        let errorMessage = 'This verification link is invalid or has expired.';
        
        if (error.code === 'auth/expired-action-code') {
          errorMessage = 'This verification link has expired. Please request a new one.';
        } else if (error.code === 'auth/invalid-action-code') {
          errorMessage = 'This verification link is invalid. Please request a new one.';
        }
        
        toast.error(errorMessage);
        setIsValidating(false);
        setTimeout(() => navigate('/'), 5000);
      }
    };

    validateCode();
  }, [auth, navigate]);

  const handleVerifyEmail = async () => {
    if (!oobCode) return;

    try {
      // Apply the action code (verify the email)
      await applyActionCode(auth, oobCode);
      
      // Force refresh the user to get updated emailVerified status
      await auth.currentUser?.reload();
      const user = auth.currentUser;

      if (user) {
        // Call backend to grant credits
        const functions = getFunctions();
        const grantCredits = httpsCallable(functions, 'emailVerificationComplete');
        
        try {
          const result = await grantCredits({ userId: user.uid });
          
          if (result.data.success) {
            if (result.data.alreadyGranted) {
              toast.info('Your email is already verified!');
            } else if (result.data.creditsGranted > 0) {
              toast.success(`Email verified! You've received ${result.data.creditsGranted} free credits! 🎉`);
            } else {
              toast.info(result.data.message || 'Email verified successfully!');
            }
          } else {
            toast.info(result.data.message || 'Email verified successfully!');
          }
        } catch (backendError) {
          console.error('Error granting credits:', backendError);
          toast.warning('Email verified, but there was an issue granting credits. Please contact support.');
        }
      }

      setIsVerified(true);
    } catch (error) {
      console.error('Error verifying email:', error);
      
      let errorMessage = 'Failed to verify email. Please try again.';
      if (error.code === 'auth/expired-action-code') {
        errorMessage = 'This verification link has expired. Please request a new one.';
      } else if (error.code === 'auth/invalid-action-code') {
        errorMessage = 'This verification link is invalid. Please request a new one.';
      }
      
      toast.error(errorMessage);
      setTimeout(() => navigate('/'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-teal-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Validating State */}
          {isValidating && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-100 rounded-full mb-4">
                <Loader className="w-8 h-8 text-cyan-600 animate-spin" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Validating Link
              </h2>
              <p className="text-gray-600">
                Please wait while we validate your verification link...
              </p>
            </div>
          )}

          {/* Invalid Code State */}
          {!isValidating && !isValidCode && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Invalid Link
              </h2>
              <p className="text-gray-600 mb-4">
                This verification link is invalid or has expired.
              </p>
              <p className="text-sm text-gray-500">
                Redirecting to home page...
              </p>
            </div>
          )}

          {/* Valid Code - Ready to Verify */}
          {!isValidating && isValidCode && !isVerified && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-full mb-4">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verify Your Email
              </h2>
              <p className="text-gray-600 mb-2">
                Click the button below to verify your email address:
              </p>
              <p className="text-cyan-700 font-semibold mb-6">
                {email}
              </p>
              <button
                onClick={handleVerifyEmail}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-600 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-teal-700 transition-all shadow-md hover:shadow-lg"
              >
                <CheckCircle className="w-5 h-5" />
                Verify My Email
              </button>
            </div>
          )}

          {/* Verified State */}
          {isVerified && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Email Verified! 🎉
              </h2>
              <p className="text-gray-600 mb-6">
                Your email has been successfully verified and your credits have been added to your account.
              </p>
              <button
                onClick={() => navigate('/generate')}
                className="w-full px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-600 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-teal-700 transition-all shadow-md hover:shadow-lg"
              >
                Go to Dashboard
              </button>
            </div>
          )}
        </div>

        {/* Additional Info */}
        {!isValidating && isValidCode && !isVerified && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              By verifying your email, you'll receive{' '}
              <span className="font-bold text-cyan-600">100 FREE credits</span>{' '}
              to start creating amazing designs!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
